import { memo, useEffect, useMemo, useState } from 'react';
import { ChevronDown, AlertTriangle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { formatDistanceToNow, parseISO, differenceInSeconds } from 'date-fns';
import { ShopChangelogDoc } from '../../types/shopChangelog';
import { useIsMobile } from '../../hooks/useIsMobile';
import { cn } from '../../utils/cn';
import { ShopChangelogUser } from './ShopChangelogUser';
import { ShopChangelogClusterPill } from './ShopChangelogClusterPill';
import { ShopChangelogPagePill } from './ShopChangelogPagePill';
import { ShopChangelogDetail } from './ShopChangelogDetail';
import { isDestructive, summariseTotals, SummaryToken } from './priceFormat';

interface ShopChangelogFeedItemProps {
  doc: ShopChangelogDoc;
  /** Highlights freshly-arrived rows merged from the polling banner. */
  isNew?: boolean;
}

const SUMMARY_TONE_CLASS: Record<SummaryToken['tone'], string> = {
  add:    'text-green-300',
  remove: 'text-red-300',
  modify: 'text-yellow-300',
  rename: 'text-yellow-200',
};

export const ShopChangelogFeedItem = memo(function ShopChangelogFeedItem({
  doc,
  isNew = false,
}: ShopChangelogFeedItemProps) {
  const isMobile = useIsMobile();
  const [isExpanded, setIsExpanded] = useState(false);

  const localTimestamp = useMemo(() => parseISO(doc['@timestamp']), [doc]);
  const [timeAgo, setTimeAgo] = useState('');

  useEffect(() => {
    const update = () => {
      const seconds = differenceInSeconds(new Date(), localTimestamp);
      if (seconds < 60) {
        setTimeAgo(seconds <= 1 ? '1 second ago' : `${seconds} seconds ago`);
      } else {
        setTimeAgo(formatDistanceToNow(localTimestamp, { addSuffix: true }));
      }
    };
    update();
    const id = window.setInterval(update, 30_000);
    return () => window.clearInterval(id);
  }, [localTimestamp]);

  const summary = useMemo(() => summariseTotals(doc.totals), [doc.totals]);
  const destructive = isDestructive(doc.totals);
  const visiblePages = doc.affectedPageIds.slice(0, 3);
  const remainingPageCount = Math.max(0, doc.affectedPageIds.length - visiblePages.length);

  const toggle = () => setIsExpanded((v) => !v);

  return (
    <div
      className={cn(
        'border-b border-glass-200/10 transition-colors',
        isExpanded ? 'bg-glass-100/5' : 'hover:bg-glass-100/[0.03]',
        isNew && 'animate-fade-in bg-accent-500/[0.05]',
      )}
    >
      {isMobile ? (
        <button
          type="button"
          onClick={toggle}
          className="w-full text-left px-3 py-3 flex flex-col gap-1.5"
        >
          <div className="flex items-center justify-between gap-2">
            <ShopChangelogUser userId={doc.userId} userIdType={doc.userIdType} inline />
            <span className="text-[10px] text-glass-500" title={localTimestamp.toLocaleString()}>
              {timeAgo}
            </span>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <ShopChangelogClusterPill clusterId={doc.clusterId} size="sm" />
            <span className="text-[10px] uppercase tracking-wide text-glass-400">{doc.networkId}</span>
            {destructive && <DestructiveBadge size="sm" />}
            <ChevronDown
              className={cn('w-3.5 h-3.5 text-glass-400 ml-auto transition-transform', isExpanded && 'rotate-180')}
            />
          </div>
          <SummaryLine
            summary={summary}
            visiblePages={visiblePages}
            remainingPageCount={remainingPageCount}
          />
        </button>
      ) : (
        <button
          type="button"
          onClick={toggle}
          className="w-full text-left px-4 py-2.5 grid items-center gap-3"
          style={{ gridTemplateColumns: 'minmax(180px, 220px) 1fr auto auto' }}
        >
          <div className="min-w-0">
            <ShopChangelogUser userId={doc.userId} userIdType={doc.userIdType} inline showAvatar />
          </div>

          <div className="min-w-0">
            <SummaryLine
              summary={summary}
              visiblePages={visiblePages}
              remainingPageCount={remainingPageCount}
            />
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {destructive && <DestructiveBadge />}
            <ShopChangelogClusterPill clusterId={doc.clusterId} />
            <span
              className="text-[10px] uppercase tracking-wide text-glass-400"
              title={`networkId: ${doc.networkId}`}
            >
              {doc.networkId}
            </span>
            <span
              className="text-xs text-glass-400 tabular-nums w-28 text-right"
              title={localTimestamp.toLocaleString()}
            >
              {timeAgo}
            </span>
          </div>

          <ChevronDown
            className={cn('w-4 h-4 text-glass-400 transition-transform shrink-0', isExpanded && 'rotate-180')}
          />
        </button>
      )}

      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            key="detail"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{ overflow: 'hidden' }}
          >
            <div className="px-4 pb-4 pt-1 md:pl-[244px]">
              <ShopChangelogDetail doc={doc} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

function SummaryLine({
  summary,
  visiblePages,
  remainingPageCount,
}: {
  summary: SummaryToken[];
  visiblePages: string[];
  remainingPageCount: number;
}) {
  if (summary.length === 0 && visiblePages.length === 0) {
    return <span className="text-sm text-glass-400 italic">No effective changes</span>;
  }

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0 text-sm">
      {summary.map((tok, i) => (
        <span key={i} className={cn('font-semibold', SUMMARY_TONE_CLASS[tok.tone])}>
          {tok.text}
          {i < summary.length - 1 && <span className="text-glass-500 font-normal">,</span>}
        </span>
      ))}
      {visiblePages.length > 0 && (
        <>
          <span className="text-glass-400">on</span>
          {visiblePages.map((p) => (
            <ShopChangelogPagePill key={p} pageId={p} />
          ))}
          {remainingPageCount > 0 && (
            <span className="text-xs text-glass-400">+{remainingPageCount} more</span>
          )}
        </>
      )}
    </div>
  );
}

function DestructiveBadge({ size = 'md' }: { size?: 'sm' | 'md' }) {
  const isSm = size === 'sm';
  return (
    <span
      title="Destructive change (pages or entries removed)"
      className={cn(
        'inline-flex items-center gap-1 rounded-full border font-semibold',
        'bg-red-500/15 text-red-300 border-red-500/25',
        isSm ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-xs',
      )}
    >
      <AlertTriangle className={isSm ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
      destructive
    </span>
  );
}
